import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../App';
import { getStudents } from '../../services/studentService';
import { getUsers } from '../../services/userService';
import { getSchools } from '../../services/schoolService';
import useLiveJobs from '../../hooks/useLiveJobs';
import NavIcon from './NavIcon';
import ProfileAvatar from './ProfileAvatar';
import ProfileModal from './ProfileModal';
import logoImg from '../../static/images/logo.png';

export const ROLE_LABELS = { 1: 'Director', 2: 'SPED Teacher' };

const AVATAR_TINTS = [
  'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200',
  'bg-pink-100 text-pink-800 dark:bg-pink-500/20 dark:text-pink-200',
  'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200',
];

export function initials(name) {
  const parts = (name || '').replace(/\(.*?\)/g, '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function minutesLeft(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return null;
  return s < 90 ? '<2 min' : `~${Math.round(s / 60)} min`;
}

// Totals for the nav badges; refreshed every 5 minutes.
function useNavCounts(isDirector) {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let alive = true;
    const load = () => {
      Promise.allSettled([
        getStudents({ page: 1, limit: 1 }),
        isDirector ? getUsers({ page: 1, limit: 1 }) : Promise.resolve(null),
        isDirector ? getSchools({ page: 1, limit: 1, status: 'active' }) : Promise.resolve(null),
      ]).then(([students, users, schools]) => {
        if (!alive) return;
        const total = (r) => (r.status === 'fulfilled' ? r.value?.data?.data?.total : undefined);
        setCounts({ students: total(students), users: total(users), schools: total(schools) });
      });
    };
    load();
    const t = setInterval(load, 300000);
    return () => { alive = false; clearInterval(t); };
  }, [isDirector]);
  return counts;
}

function CountBadge({ children, live = false }) {
  return (
    <span className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-[7px] text-[11px] font-semibold leading-[18px] whitespace-nowrap ${
      live
        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
    }`}
    >
      {live && <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 dark:bg-indigo-400 ring-[3px] ring-indigo-600/20 animate-pulse" />}
      {children}
    </span>
  );
}

function NavItem({ to, icon, label, badge, live, collapsed, onNavigate, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? (badge != null ? `${label} · ${badge}` : label) : undefined}
      className={({ isActive }) => [
        'relative flex items-center rounded-[10px] text-[13.5px] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        collapsed ? 'justify-center w-10 h-10 mx-auto' : 'gap-[11px] px-2.5 py-2',
        isActive
          ? 'bg-indigo-50 text-indigo-800 font-semibold dark:bg-indigo-500/15 dark:text-indigo-200'
          : 'text-slate-600 font-medium hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white',
      ].join(' ')}
    >
      {({ isActive }) => (
        <>
          {isActive && !collapsed && (
            <span className="absolute -left-3 top-[7px] bottom-[7px] w-[3px] rounded-r bg-indigo-600 dark:bg-indigo-400" aria-hidden="true" />
          )}
          <NavIcon name={icon} className="w-[19px] h-[19px]" />
          {!collapsed && <span className="truncate">{label}</span>}
          {!collapsed && badge != null && <CountBadge live={live}>{badge}</CountBadge>}
          {collapsed && live && (
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-indigo-600 ring-2 ring-white animate-pulse dark:ring-slate-900" />
          )}
        </>
      )}
    </NavLink>
  );
}

function GroupLabel({ children, aside }) {
  return (
    <div className="flex items-center px-2.5 pt-3.5 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
      <span className="flex-1">{children}</span>
      {aside}
    </div>
  );
}

function UserCard({ user, collapsed }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const ref = useRef(null);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Account';
  const roleLabel = ROLE_LABELS[user?.role] || 'User';

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenuOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menuOpen]);

  return (
    <div ref={ref} className="relative border-t border-slate-100 dark:border-slate-800">
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={collapsed ? `${fullName} · ${roleLabel}` : undefined}
        className={`w-full flex items-center gap-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${collapsed ? 'justify-center py-3' : 'px-3.5 py-3'}`}
      >
        <span className="w-[34px] h-[34px] rounded-[10px] overflow-hidden flex-shrink-0">
          <ProfileAvatar type="user" id={user?.id} variant="solid" hasProfileImage={user?.has_profile_image}
            containerClassName="w-full h-full" className="w-full h-full object-cover block" />
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900 dark:text-white truncate">{fullName}</span>
              <span className="block text-[11.5px] text-slate-500 dark:text-slate-400 truncate">
                {roleLabel}{user?.school_name ? ` · ${user.school_name}` : ''}
              </span>
            </span>
            <NavIcon name="more" className="w-5 h-5 text-slate-400" />
          </>
        )}
      </button>
      {menuOpen && (
        <div role="menu" className={`absolute z-50 bottom-full mb-2 w-56 rounded-xl border border-slate-200 bg-white shadow-xl dark:bg-slate-800 dark:border-slate-700 overflow-hidden ${collapsed ? 'left-2' : 'left-3 right-3 w-auto'}`}>
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{fullName}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{user?.email}</p>
          </div>
          <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setShowProfile(true); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700">
            <NavIcon name="user" className="w-4 h-4" /> View profile
          </button>
          <button type="button" role="menuitem" onClick={() => { logout(); navigate('/login'); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 border-t border-slate-100 dark:border-slate-700">
            <NavIcon name="logout" className="w-4 h-4" /> Log out
          </button>
        </div>
      )}
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </div>
  );
}

function LiveJobCard({ job, onNavigate }) {
  const pct = Math.max(0, Math.min(100, Number(job.overall_pct) || 0));
  const eta = minutesLeft(job.remaining_seconds);
  const what = job.job_type === 'batch_upload' ? 'batch upload' : 'compliance run';
  return (
    <Link
      to="/pipeline-jobs"
      onClick={onNavigate}
      className="block mx-3 mb-2.5 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2.5 hover:border-indigo-200 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:hover:border-indigo-500/40 transition-colors"
    >
      <span className="flex items-center gap-[7px] text-[11px] font-semibold uppercase tracking-[0.05em] text-indigo-700 dark:text-indigo-300">
        <span className="w-[7px] h-[7px] rounded-full bg-indigo-600 dark:bg-indigo-400 ring-[3px] ring-indigo-600/20 animate-pulse" />
        Processing
        {eta && <span className="ml-auto normal-case tracking-normal font-medium text-indigo-500 dark:text-indigo-300/80">{eta}</span>}
      </span>
      <span className="block mt-1.5 text-[13px] font-semibold text-slate-900 dark:text-white truncate">
        {job.student_name || `Student #${job.student_id}`} · {what}
      </span>
      <span className="block text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{job.message || 'Working…'}</span>
      <span className="block h-[5px] mt-[7px] rounded-full bg-indigo-100 dark:bg-indigo-500/20" aria-hidden="true">
        <span className="block h-full rounded-full bg-indigo-600 dark:bg-indigo-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </span>
      <span className="sr-only">{pct}% complete</span>
    </Link>
  );
}

const RECENT_LIMIT = 3;

// Students whose pipeline is running, then students whose pipeline finished in the last 24 hours.
function recentFromJobs(live) {
  const seen = new Set();
  const out = [];
  const push = (job, phase) => {
    const id = job?.student_id;
    const name = job?.student_name;
    if (id == null || !name || seen.has(String(id))) return;
    seen.add(String(id));
    const failed = phase === 'ended' && String(job.status || '').toLowerCase() !== 'completed';
    out.push({ id, name, phase, failed });
  };
  (live.running || []).forEach((job) => push(job, 'started'));
  (live.recent || []).forEach((job) => push(job, 'ended'));
  return out.slice(0, RECENT_LIMIT);
}

export default function Sidebar({ isOpen, onClose, collapsed = false, onToggleCollapse, overlayOnDesktop = false }) {
  const { user } = useAuth();
  const isDirector = user?.role === 1;
  const canSeeJobs = user?.role === 1 || user?.role === 2;
  const counts = useNavCounts(isDirector);
  const live = useLiveJobs();
  const recent = canSeeJobs ? recentFromJobs(live) : [];

  const runningJob = live.running?.[0];
  const runningCount = live.counts?.running || 0;
  // The drawer on small screens is always full width; collapse applies from lg up.
  const rail = collapsed && !isOpen;

  useEffect(() => {
    if (!isOpen) return undefined;
    const esc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  return (
    <>
      {isOpen && (
        <div className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[45] ${overlayOnDesktop ? "" : "lg:hidden"}`} onClick={onClose} aria-hidden="true" />
      )}

      <aside
        aria-label="Main navigation"
        className={[
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800',
          'transition-[transform,width] duration-300 ease-in-out',
          isOpen ? 'translate-x-0 w-[272px] shadow-2xl' : '-translate-x-full lg:translate-x-0',
          !isOpen && (rail ? 'lg:w-[68px]' : 'lg:w-[248px]'),
        ].filter(Boolean).join(' ')}
      >
        <div className={`h-16 flex-shrink-0 flex items-center border-b border-slate-100 dark:border-slate-800 ${rail ? 'justify-center px-2' : 'px-3.5'}`}>
          {!rail && (
            <Link to={getDashboardPath(user?.role)} onClick={onClose} className="flex items-center min-w-0" aria-label="Athenian Academy — dashboard">
              <img src={logoImg} alt="Athenian Academy" className="h-10 w-auto max-w-[168px] object-contain object-left dark:bg-white dark:rounded-lg dark:px-1" />
            </Link>
          )}
          {/* One menu button, always top right: it opens and closes the nav. */}
          <button
            type="button"
            onClick={isOpen ? onClose : onToggleCollapse}
            aria-label={rail ? 'Open navigation' : isOpen ? 'Close navigation' : 'Collapse navigation'}
            aria-expanded={!rail}
            title={rail ? 'Open navigation' : isOpen ? 'Close navigation' : 'Collapse navigation'}
            className={`${rail ? '' : 'ml-auto'} w-9 h-9 flex items-center justify-center rounded-[10px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500`}
          >
            <NavIcon name="menu" className="w-5 h-5" />
          </button>
        </div>

        <nav className={`flex-1 overflow-y-auto overflow-x-hidden pt-3.5 pb-1.5 flex flex-col gap-0.5 ${rail ? 'px-2' : 'px-3'}`}>
          <NavItem to={getDashboardPath(user?.role)} icon="home" label="Dashboard" collapsed={rail} onNavigate={onClose} />
          <NavItem to="/students" icon="students" label="Students" badge={counts.students} collapsed={rail} onNavigate={onClose} />
          {canSeeJobs && (
            <NavItem
              to="/pipeline-jobs"
              icon="jobs"
              label="Pipeline Jobs"
              badge={runningCount ? 'Running' : undefined}
              live={runningCount > 0}
              collapsed={rail}
              onNavigate={onClose}
            />
          )}

          {isDirector && (
            <>
              {rail ? <div className="my-2 mx-2 border-t border-slate-100 dark:border-slate-800" /> : <GroupLabel>Administration</GroupLabel>}
              <NavItem to="/users" icon="users" label="Users" badge={counts.users} collapsed={rail} onNavigate={onClose} />
              <NavItem to="/schools" icon="schools" label="Schools" badge={counts.schools} collapsed={rail} onNavigate={onClose} />
              <NavItem to="/admin/eligibility-templates" icon="templates" label="Eligibility Templates" collapsed={rail} onNavigate={onClose} />
            </>
          )}

          {!rail && recent.length > 0 && (
            <>
              <GroupLabel aside={
                <Link to="/students" onClick={onClose} className="normal-case tracking-normal font-medium hover:text-indigo-600 dark:hover:text-indigo-300">View all</Link>
              }
              >
                Recent students
              </GroupLabel>
              {recent.map((s, i) => (
                <NavLink
                  key={s.id}
                  to={`/students/${s.id}/information`}
                  onClick={onClose}
                  className={({ isActive }) => `flex items-center gap-[11px] px-2.5 py-1.5 rounded-[10px] text-[13.5px] font-medium transition-colors ${
                    isActive ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                  }`}
                >
                  <span className={`w-[22px] h-[22px] rounded-full inline-flex items-center justify-center text-[9.5px] font-bold flex-shrink-0 ${AVATAR_TINTS[i % AVATAR_TINTS.length]}`}>
                    {initials(s.name)}
                  </span>
                  <span className="flex-1 truncate">{s.name}</span>
                  {s.phase === 'started' ? (
                    <span className="w-[7px] h-[7px] rounded-full bg-indigo-600 dark:bg-indigo-400 ring-[3px] ring-indigo-600/20 animate-pulse" title="Processing" />
                  ) : (
                    <span className={`w-[7px] h-[7px] rounded-full ${s.failed ? 'bg-red-600' : 'bg-green-600'}`} title={s.failed ? 'Failed' : 'Finished'} />
                  )}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        {!rail && runningJob && <LiveJobCard job={runningJob} onNavigate={onClose} />}
        <UserCard user={user} collapsed={rail} />
        {!rail && (
          <p className="flex items-center gap-1.5 px-3.5 pb-3 text-[10.5px] text-slate-400 dark:text-slate-500">
            <NavIcon name="shield" className="w-3.5 h-3.5" /> HIPAA &amp; FERPA compliant
          </p>
        )}
      </aside>
    </>
  );
}
